<template>
  <div
    :class="[
      'cy-popover',
      `popover-${popoverId}`,
      `popover-align-${computedAlign}`,
      { 'popover-arrow': props.withArrow },
    ]"
    :style="{
      ...getStyle(
        [
          'cy-list',
          `popover-align-${computedAlign}`,
          props.withArrow ? 'popover-arrow' : '',
        ],
        props.styleObj
      ),
    }"
  >
    <div
      ref="refTrigger"
      class="popover_trigger"
      :style="{ ...getStyle(['popover_trigger'], props.styleObj) }"
      @click="togglePopover"
    >
      <template v-if="$slots.trigger">
        <slot name="trigger" />
      </template>
    </div>
    <div
      ref="refContent"
      :class="[
        'popover_content',
        { 'content-show': state },
        `content-position-${computedPostion}`,
      ]"
      :style="{
        ...getStyle(
          [
            'popover_content',
            `content-position-${computedPostion}`,
            state ? 'content-show' : '',
          ],
          props.styleObj,
        ),
        textAlign: locale === 'zh-CN' ? 'justify' : 'unset'
      }"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { locale } from "@/locale";
import type { PopoverPosition, PopoverAlign } from "./popover";
import { watch, watchPostEffect, onMounted, onUnmounted, computed, ref } from "vue";
import { getStyle, getWindowOffset } from "./popover";

const OFFSET_SCROLLBAR = 20;
const props = withDefaults(
  defineProps<{
    id?: string;
    state?: boolean;
    position?: PopoverPosition;
    align?: PopoverAlign;
    withArrow?: boolean;
    autoDetect?: boolean;
    styleObj?: Record<string, any> | undefined;
  }>(),
  {
    id: function () {
      return `${Date.now()}-${Math.ceil(Math.random() * 1000)}`;
    },
    position: "bottom",
    align: "begin",
    withArrow: true,
    autoDetect: true,
    styleObj: undefined,
  }
);

const emit = defineEmits(["update:state", "toggle", "show", "hide"]);
const popoverId = ref(props.id);
const state = ref(props.state);
const autoAdjust = ref({
  position: "",
  align: "",
});

const refTrigger = ref();
const refContent = ref();

const computedPostion = computed(() => {
  return autoAdjust.value.position || props.position;
});
const computedAlign = computed(() => {
  return autoAdjust.value.align || props.align;
});

const _detectX = () => {
  const trigger = refTrigger.value;
  const content = refContent.value;
  const windowOffset_trigger = getWindowOffset(trigger);

  if (windowOffset_trigger.left - content.scrollWidth < 0) {
    if (props.position === "left") {
      return {
        position: "right",
      };
    }
    if (props.position === "top" || props.position === "bottom") {
      return {
        align: "begin",
      };
    }
  } else if (
    windowOffset_trigger.left +
      trigger.scrollWidth +
      content.scrollWidth +
      OFFSET_SCROLLBAR >
    window.innerWidth
  ) {
    if (props.position === "right") {
      return {
        position: "left",
      };
    }
    if (props.position === "top" || props.position === "bottom") {
      return {
        align: "end",
      };
    }
  }
  return {};
};

const _detectY = () => {
  const trigger = refTrigger.value;
  const content = refContent.value;
  const windowOffset_trigger = getWindowOffset(trigger);

  if (windowOffset_trigger.top - content.scrollHeight < 0) {
    if (props.position === "top") {
      return {
        position: "bottom",
      };
    }
    if (props.position === "left" || props.position === "right") {
      return {
        align: "begin",
      };
    }
  } else if (
    windowOffset_trigger.top +
      trigger.scrollHeight +
      content.scrollHeight +
      OFFSET_SCROLLBAR >
    window.innerHeight
  ) {
    if (props.position === "bottom") {
      return {
        position: "top",
      };
    }
    if (props.position === "left" || props.position === "right") {
      return {
        align: "end",
      };
    }
  }
  return {};
};

const _resize = () => {
  if (props.autoDetect) {
    autoAdjust.value = {
      ...autoAdjust.value,
      ..._detectX(),
      ..._detectY(),
    };
  }
};

const _clickListener = (event: { composedPath: () => any[] }) => {
  let result = event.composedPath().find((node) => {
    if (node.classList) {
      return node.classList.value.search(`popover-${popoverId.value}`) > 0;
    }
    return false;
  });
  if (typeof result == "undefined") {
    togglePopover();
  }
};

const togglePopover = () => {
  let newState = !state.value;
  state.value = newState;
  if (newState) {
    emit("show");
  } else {
    emit("hide");
  }
  emit("toggle", newState);
  emit("update:state", newState);
};

watch(
  () => computedPostion.value,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      _resize();
    }
  }
);

watch(
  () => computedAlign.value,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      _resize();
    }
  }
);

watch(
  () => state.value,
  (newValue) => {
    window.removeEventListener("click", _clickListener);
    if (newValue) {
      window.addEventListener("click", _clickListener);
    }
    if (state.value !== newValue) {
      state.value = newValue;
    }
  }
);

watch(
  () => props.state,
  (newValue) => {
    if (typeof props.state !== "undefined") {
      state.value = newValue;
    }
  }
);

watchPostEffect(() => {
  if (state.value) {
    _resize();
  }
});

onMounted(() => {
  _resize();
  window.addEventListener("resize", _resize);
  window.addEventListener("scroll", _resize);
});

onUnmounted(() => {
  window.removeEventListener("resize", _resize);
  window.removeEventListener("scroll", _resize);
  window.removeEventListener("click", _clickListener);
});
</script>

<style lang="less">
.cy-popover {
  position: relative;
  display: inline-flex;
  vertical-align: middle;
  color: #666;
  &.popover-arrow {
    .popover_content::before {
      content: "";
    }
  }

  &.popover-align-begin {
    align-items: flex-start;
    justify-content: flex-start;

    &.popover-arrow {
      .content-position-bottom,
      .content-position-top {
        &::before {
          left: 5px;
        }
      }

      .content-position-left,
      .content-position-right {
        &::before {
          top: 5px;
        }
      }
    }
  }

  &.popover-align-center {
    align-items: center;
    justify-content: center;

    &.popover-arrow {
      .content-position-bottom,
      .content-position-top {
        &::before {
          left: calc((100% - 14px) / 2);
        }
      }

      .content-position-left,
      .content-position-right {
        &::before {
          top: calc((100% - 14px) / 2);
        }
      }
    }
  }

  &.popover-align-end {
    align-items: flex-end;
    justify-content: flex-end;

    &.popover-arrow {
      .content-position-bottom,
      .content-position-top {
        &::before {
          right: 5px;
        }
      }

      .content-position-left,
      .content-position-right {
        &::before {
          bottom: 5px;
        }
      }
    }
  }

  .popover_trigger {
    cursor: pointer;
    flex-grow: 1;
  }

  .popover_content {
    display: none;
    position: absolute;
    max-width: 300px;
    min-width: 280px;
    background-color: #fff;
    padding: 12px 16px;
    font-size: 14px;
    z-index: 1;
    box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.15);
    text-align: justify;
    &.content-show {
      display: block;
    }

    &::before {
      position: absolute;
      border-style: solid;
    }

    &.content-position-bottom {
      top: 100%;
      margin-top: 6px;

      &::before {
        top: -6px;
        border-color: #fff transparent;
        border-width: 0 6px 6px;
      }
    }

    &.content-position-top {
      bottom: 100%;
      margin-bottom: 6px;

      &::before {
        bottom: -6px;
        border-color: #fff transparent;
        border-width: 6px 6px 0;
      }
    }

    &.content-position-left {
      right: 100%;
      margin-right: 6px;

      &::before {
        right: -6px;
        border-color: transparent #fff;
        border-width: 6px 0 6px 6px;
      }
    }

    &.content-position-right {
      left: 100%;
      margin-left: 6px;

      &::before {
        left: -6px;
        border-color: transparent #fff;
        border-width: 6px 6px 6px 0;
      }
    }
  }
}
</style>
